import { supabase } from '@/lib/supabase';
import { placeOrderFromCart } from '@/lib/chatOrder';
import { pushNotification } from '@/lib/notifications';
import { attemptBookingAcross } from '@/lib/chatBooking';
import { addDaysISO, todayISO } from '@/lib/hebergement';
import type { CartItem } from '@/hooks/useCart';

// ─────────────────────────────────────────────────────────────
// ZIFEK — Pack voyage composé par l'assistant IA.
//
// « Je veux aller à Fès pour le week-end, budget 2000 dirhams » :
// 1. on comprend la destination, les dates et le budget ;
// 2. on assemble un pack complet avec les vrais commerces de la
//    plateforme (hébergement, guide, chauffeur, activité/restaurant) ;
// 3. l'utilisateur voit le détail et peut tout réserver d'un clic :
//    une commande par commerce + réservation d'hébergement + RDV,
//    et chaque commerce reçoit sa notification.
// ─────────────────────────────────────────────────────────────

export type PackItemKind = 'hebergement' | 'guide' | 'chauffeur' | 'activite' | 'restaurant' | 'service';

export interface PackItem {
  kind: PackItemKind;
  /** Identifiant de l'entité source (hébergement, service, RDV…). */
  entityId: number;
  /** Identifiant stable pour le panier/commande (évite les collisions). */
  cartId: number;
  name: string;
  merchantId: number | null;
  merchantName: string | null;
  city: string | null;
  price: number;
  currency: string;
  image: string | null;
  /** Ex. « 2 nuits × 450 MAD », « Sortie guidée 3 h ». */
  detail: string;
  /** Service ZCalendar réservable (si applicable). */
  bookingServiceId?: number | null;
  /** Dates propres à l'article (hébergement). */
  checkIn?: string;
  checkOut?: string;
  nights?: number;
}

export interface TravelPack {
  destination: string;
  budget: number;
  currency: string;
  nights: number;
  /** Nombre de jours du séjour (jours = nuits + 1 par défaut). */
  days: number;
  checkIn: string;
  checkOut: string;
  guests: number;
  items: PackItem[];
  total: number;
  withinBudget: boolean;
  notes: string[];
  /** Programme détaillé jour par jour. */
  program: PackProgramDay[];
}

export interface PackProgramDay {
  day: number;
  date: string;
  title: string;
  lines: string[];
}

const TRAVEL_RE = /\b(voyage|voyager|week-?end|vacances|s[ée]jour|partir|escapade|tourisme|visiter|d[ée]couvrir|aller\s+[àa])\b/i;

/** Détecte une demande de voyage / séjour / week-end (pack complet). */
export function isTravelPackRequest(text: string): boolean {
  const t = (text || '').trim();
  if (!t || t.length < 6) return false;
  // On ne détourne pas les demandes de rendez-vous ni de panier.
  if (/(rendez[- ]?vous|\brdv\b)/i.test(t)) return false;
  if (/(panier|\bcommande\b|ajoute|supprime\s+du\s+panier)/i.test(t)) return false;
  return TRAVEL_RE.test(t);
}

const STOP_WORDS = new Set([
  'pour', 'avec', 'budget', 'ce', 'cet', 'cette', 'le', 'la', 'les', 'du', 'de', 'des',
  'en', 'et', 'au', 'aux', 'un', 'une', 'mon', 'ma', 'mes', 'notre', 'nos', 'j', 'je',
  'nous', 'sur', 'pendant', 'dans', 'a', 'à', 'dh', 'dhs', 'dirham', 'dirhams', 'mad',
  'euro', 'euros', 'eur', 'usd', 'dollar', 'dollars', 'week', 'end', 'weekend',
]);

function fold(value: string): string {
  return (value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function toNumber(raw: string): number {
  const cleaned = (raw || '').replace(/[^0-9.,]/g, '').replace(/\s/g, '');
  // 2.000 / 2,000 → 2000 ; 2.500,50 → 2500.5
  const normalized = cleaned.replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
  const value = Number(normalized);
  return Number.isFinite(value) ? value : 0;
}

export interface TravelIntent {
  destination: string;
  budget: number;
  currency: string;
  nights: number;
  days: number;
  checkIn: string;
  checkOut: string;
  guests: number;
  weekend: boolean;
}

/** Comprend la destination, le budget, la durée et les dates. */
export function parseTravelIntent(text: string): TravelIntent {
  const t = text || '';
  const folded = fold(t);

  // ── Destination : mot(s) après une préposition de lieu ──
  let destination = '';
  const m = t.match(/(?:aller|partir|voyage(?:r)?|visiter|d[ée]couvrir|direction|escapade|s[ée]jour|week-?end)\s+(?:à|a|au|aux|vers|pour|en|sur)?\s*([A-Za-zÀ-ÿ'’\- ]{2,40})/i)
    || t.match(/(?:à|a|au|aux|vers|pour)\s+([A-Za-zÀ-ÿ'’\- ]{3,40})/i);
  if (m && m[1]) {
    const words = m[1]
      .replace(/[.,;:!?].*$/, '')
      .split(/\s+/)
      .filter(Boolean);
    const kept: string[] = [];
    for (const w of words) {
      if (STOP_WORDS.has(fold(w))) break;
      if (/\d/.test(w)) break;
      kept.push(w);
      if (kept.length >= 3) break;
    }
    destination = kept.join(' ').trim();
  }
  if (!destination) {
    // Repli : première ville citée qui ressemble à un nom propre.
    const cap = t.match(/\b([A-ZÀ-Ý][a-zà-ÿ'’\-]{2,})\b/);
    if (cap) destination = cap[1];
  }

  // ── Budget ──
  let budget = 0;
  const budgetMatch = t.match(/(?:budget|enveloppe|dispose[rz]?\s+de|j'?ai)\s*(?:de|d'|:)?\s*([0-9][0-9\s.,]*)/i)
    || t.match(/([0-9][0-9\s.,]{1,12})\s*(?:dh|dhs|dirhams?|mad|euros?|eur|usd|dollars?)/i);
  if (budgetMatch && budgetMatch[1]) budget = toNumber(budgetMatch[1]);

  // ── Devise ──
  let currency = 'MAD';
  if (/(eur|euro|euros)/i.test(t)) currency = 'EUR';
  else if (/(usd|dollar|dollars)/i.test(t)) currency = 'USD';
  else if (/(xof|franc\s*cfa)/i.test(t)) currency = 'XOF';

  // ── Durée ──
  const weekend = /week-?end/i.test(folded);
  let nights = weekend ? 2 : 1;
  let days = 0;
  const daysMatch = folded.match(/(\d{1,2})\s*jours?/);
  if (daysMatch) days = Math.min(15, Math.max(1, parseInt(daysMatch[1], 10) || 1));
  const nightsMatch = folded.match(/(\d{1,2})\s*(nuits?|jours?)/);
  if (nightsMatch) {
    const n = parseInt(nightsMatch[1], 10) || 1;
    nights = /nuit/.test(nightsMatch[2]) ? n : Math.max(1, n - 1);
  }
  nights = Math.min(14, Math.max(1, nights));
  if (!days) days = nights + 1;
  days = Math.min(15, Math.max(nights + 1, days));

  const guestsMatch = t.match(/(\d{1,2})\s*(?:personnes?|adultes?|voyageurs?|pax)/i);
  const guests = guestsMatch ? Math.max(1, parseInt(guestsMatch[1], 10) || 1) : 2;

  // ── Dates : week-end = prochain vendredi, sinon demain ──
  let checkIn = addDaysISO(todayISO(), 1);
  if (weekend) {
    const d = new Date();
    const day = d.getDay(); // 0 = dimanche
    let diff = (5 - day + 7) % 7;
    if (diff === 0) diff = 7;
    checkIn = addDaysISO(todayISO(), diff);
  }
  const checkOut = addDaysISO(checkIn, nights);

  return { destination, budget, currency, nights, days, checkIn, checkOut, guests, weekend };
}

/** Construit le programme jour par jour à partir des prestations du pack. */
function buildProgram(params: {
  destination: string;
  checkIn: string;
  nights: number;
  days: number;
  items: PackItem[];
}): PackProgramDay[] {
  const { destination, checkIn, nights, days, items } = params;
  const totalDays = Math.max(1, days);
  const program: PackProgramDay[] = [];
  const label = (item: PackItem) => `${item.name}${item.merchantName ? ` · ${item.merchantName}` : ''}`;

  const hotel = items.find((i) => i.kind === 'hebergement');
  const guide = items.find((i) => i.kind === 'guide');
  const driver = items.find((i) => i.kind === 'chauffeur');
  const table = items.find((i) => i.kind === 'restaurant');
  const activity = items.find((i) => i.kind === 'activite');
  const extras = items.filter((i) => i.kind === 'service');

  for (let day = 1; day <= totalDays; day += 1) {
    const date = addDaysISO(checkIn, day - 1);
    const lines: string[] = [];
    let title = `Jour ${day} — ${destination}`;

    if (day === 1) {
      title = `Jour 1 — Arrivée à ${destination}`;
      if (hotel) lines.push(`Installation : ${label(hotel)} (${nights} nuit${nights > 1 ? 's' : ''})`);
      if (driver) lines.push(`Transfert / déplacements : ${label(driver)}`);
      if (table) lines.push(`Premier repas : ${label(table)}`);
    } else if (day === totalDays && totalDays > 1) {
      title = `Jour ${totalDays} — Départ`;
      if (hotel) lines.push(`Petit-déjeuner et libération du logement (${label(hotel)})`);
      if (driver) lines.push(`Transfert de départ : ${label(driver)}`);
      lines.push('Fin du séjour — bon retour !');
    } else {
      title = `Jour ${day} — Découverte de ${destination}`;
      if (guide) lines.push(`Visite guidée : ${label(guide)}`);
      if (activity) lines.push(`Activité : ${label(activity)}`);
      if (table) lines.push(`Table locale : ${label(table)}`);
      if (driver) lines.push(`Déplacements assurés par : ${label(driver)}`);
      if (lines.length === 0 && hotel) lines.push(`Journée libre — logement : ${label(hotel)}`);
    }

    extras.forEach((extra) => {
      if (day === 1) lines.push(`Service inclus : ${label(extra)}`);
    });

    if (lines.length === 0) lines.push(`Journée libre à ${destination}.`);
    program.push({ day, date, title, lines });
  }

  return program;
}

// ── Collecte des données réelles ────────────────────────────

interface PropertyRow {
  id: number;
  merchant_id: number;
  name: string;
  type: string | null;
  city: string | null;
  price: number | null;
  currency: string | null;
  cover_image: string | null;
}

interface ServiceRow {
  id: number;
  titre: string | null;
  prix: number | null;
  ville: string | null;
  owner: number | null;
  product_image: string | null;
  typeservice: string | null;
}

interface BookingRow {
  id: number;
  owner: number | null;
  service_name: string;
  price: number | null;
  currency: string | null;
  duration: number | null;
  image: string | null;
  location: string | null;
}

interface MerchantRow {
  id: number;
  name: string | null;
  nomcommerce: string | null;
  Ville: string | null;
}

/** Classe un service selon son intitulé (guide, chauffeur, restaurant…). */
function classifyService(text: string): PackItemKind {
  const t = fold(text);
  if (/(guide|tourisme|tour\b|circuit|excursion|visite)/.test(t)) return 'guide';
  if (/(chauffeur|transport|voiture|taxi|transfert|location\s+de\s+voiture)/.test(t)) return 'chauffeur';
  if (/(restaurant|cafe|diner|dejeuner|repas|traiteur|food|cuisine|degustation)/.test(t)) return 'restaurant';
  if (/(activite|atelier|spa|quad|randonnee|excursion|spectacle|musee|hammam)/.test(t)) return 'activite';
  return 'service';
}

const KIND_META: Record<PackItemKind, { label: string; icon: string }> = {
  hebergement: { label: 'Hébergement', icon: 'ri-hotel-bed-line' },
  guide: { label: 'Guide touristique', icon: 'ri-map-2-line' },
  chauffeur: { label: 'Chauffeur privé', icon: 'ri-car-line' },
  activite: { label: 'Activité', icon: 'ri-compass-3-line' },
  restaurant: { label: 'Table locale', icon: 'ri-restaurant-line' },
  service: { label: 'Service', icon: 'ri-service-line' },
};

export function packKindMeta(kind: PackItemKind) {
  return KIND_META[kind] || KIND_META.service;
}

/** Construit un pack de voyage à partir des commerces réels de la plateforme. */
export async function buildTravelPack(text: string): Promise<{ pack: TravelPack; intent: TravelIntent } | null> {
  const intent = parseTravelIntent(text);
  const target = fold(intent.destination);

  const [propRes, svcRes] = await Promise.all([
    supabase
      .from('hebergement_properties')
      .select('id, merchant_id, name, type, city, price, currency, cover_image')
      .eq('published', true)
      .eq('status', 'active')
      .limit(120),
    supabase
      .from('services')
      .select('id, titre, prix, ville, owner, product_image, typeservice')
      .limit(300),
  ]);

  const allProps = (propRes.data || []) as PropertyRow[];
  const allServices = (svcRes.data || []) as ServiceRow[];

  const matchCity = (city: string | null | undefined): boolean => {
    if (!target) return true;
    const c = fold(city || '');
    if (!c) return false;
    return c.includes(target) || target.includes(c);
  };

  let properties = allProps.filter((p) => matchCity(p.city));
  let services = allServices.filter((s) => matchCity(s.ville));
  let destination = intent.destination || (properties[0]?.city || allServices[0]?.ville || '');

  // Repli : rien sur la ville demandée → on prend la meilleure ville disponible.
  let fallbackNote = '';
  if (properties.length === 0 && services.length === 0) {
    const fallbackCity = allProps[0]?.city || allServices[0]?.ville || '';
    if (!fallbackCity) return null;
    if (intent.destination) {
      fallbackNote = `Aucune offre publiée à ${intent.destination} pour l'instant : voici le pack le plus proche, à ${fallbackCity}.`;
    }
    destination = fallbackCity;
    properties = allProps.filter((p) => fold(p.city || '') === fold(fallbackCity));
    services = allServices.filter((s) => fold(s.ville || '') === fold(fallbackCity));
  }

  // Commerces (noms + villes) pour nommer proprement chaque partie du pack.
  const ownerIds = Array.from(new Set([
    ...properties.map((p) => Number(p.merchant_id)),
    ...services.map((s) => Number(s.owner)),
  ].filter((id) => Number.isFinite(id) && id > 0))).slice(0, 80);

  const merchants = new Map<number, MerchantRow>();
  if (ownerIds.length > 0) {
    const { data } = await supabase
      .from('users')
      .select('id, name, nomcommerce, Ville')
      .in('id', ownerIds);
    ((data || []) as MerchantRow[]).forEach((m) => merchants.set(Number(m.id), m));
  }

  // Services réservables (ZCalendar) des commerces de la destination.
  const bookingServices: BookingRow[] = [];
  if (ownerIds.length > 0) {
    const { data } = await supabase
      .from('booking_services')
      .select('id, owner, service_name, price, currency, duration, image, location')
      .in('owner', ownerIds)
      .limit(60);
    ((data || []) as BookingRow[]).forEach((b) => bookingServices.push(b));
  }

  const items: PackItem[] = [];
  const notes: string[] = [];
  if (fallbackNote) notes.push(fallbackNote);
  const budget = intent.budget;
  const currency = intent.currency;

  const merchantName = (id: number | null | undefined): string | null => {
    if (!id) return null;
    const row = merchants.get(Number(id));
    return row ? (row.nomcommerce || row.name || null) : null;
  };

  // ── 1. Hébergement (le moins cher de la destination) ──
  const sortedProps = [...properties]
    .filter((p) => Number(p.price) > 0)
    .sort((a, b) => Number(a.price) - Number(b.price));
  const hotel = sortedProps[0];
  if (hotel) {
    const nightPrice = Number(hotel.price) || 0;
    const total = nightPrice * intent.nights;
    if (budget > 0 && total > budget && sortedProps.length > 1) {
      notes.push(`L'hébergement le moins cher dépasse votre budget sur ${intent.nights} nuit(s) — réduisez la durée ou augmentez le budget.`);
    }
    items.push({
      kind: 'hebergement',
      entityId: Number(hotel.id),
      cartId: 900000000 + Number(hotel.id),
      name: hotel.name,
      merchantId: Number(hotel.merchant_id),
      merchantName: merchantName(hotel.merchant_id),
      city: hotel.city,
      price: total,
      currency: (hotel.currency || currency || 'MAD').toUpperCase(),
      image: hotel.cover_image,
      detail: `${intent.nights} nuit${intent.nights > 1 ? 's' : ''} × ${nightPrice.toLocaleString('fr-FR')} ${(hotel.currency || currency).toUpperCase()} · ${intent.guests} pers.`,
      checkIn: intent.checkIn,
      checkOut: intent.checkOut,
      nights: intent.nights,
    });
  } else {
    notes.push("Aucun hébergement publié pour cette destination : le logement reste à confirmer.");
  }

  // ── 2. Guide / chauffeur / activité / restaurant ──
  const picked: PackItemKind[] = [];
  const pushService = (row: ServiceRow, kind: PackItemKind, detail: string) => {
    if (picked.includes(kind)) return;
    const price = Number(row.prix) || 0;
    picked.push(kind);
    items.push({
      kind,
      entityId: Number(row.id),
      cartId: 700000000 + Number(row.id),
      name: row.titre || packKindMeta(kind).label,
      merchantId: row.owner ? Number(row.owner) : null,
      merchantName: merchantName(row.owner),
      city: row.ville,
      price,
      currency,
      image: row.product_image,
      detail,
    });
  };

  // Chauffeur : service texte classé « chauffeur » ou service ZCalendar.
  const serviceBucket = (kind: PackItemKind) =>
    services
      .filter((s) => classifyService(`${s.titre || ''} ${s.typeservice || ''}`) === kind)
      .sort((a, b) => (Number(a.prix) || Infinity) - (Number(b.prix) || Infinity));

  const bookingFor = (kind: PackItemKind): BookingRow | null => {
    const found = bookingServices.find((b) => classifyService(`${b.service_name || ''} ${b.location || ''}`) === kind);
    return found || null;
  };

  const addFromBooking = (b: BookingRow, kind: PackItemKind, detail: string) => {
    if (picked.includes(kind)) return true;
    const price = Number(b.price) || 0;
    picked.push(kind);
    items.push({
      kind,
      entityId: Number(b.id),
      cartId: 800000000 + Number(b.id),
      name: b.service_name,
      merchantId: b.owner ? Number(b.owner) : null,
      merchantName: merchantName(b.owner),
      city: destination || null,
      price,
      currency: (b.currency || currency || 'MAD').toUpperCase(),
      image: b.image,
      detail,
      bookingServiceId: Number(b.id),
    });
    return true;
  };

  // Guide touristique
  const guideSvc = serviceBucket('guide')[0];
  const guideBook = bookingFor('guide');
  if (guideSvc) pushService(guideSvc, 'guide', 'Visite guidée de la ville');
  else if (guideBook) addFromBooking(guideBook, 'guide', 'Visite guidée de la ville');

  // Chauffeur privé
  const driverSvc = serviceBucket('chauffeur')[0];
  const driverBook = bookingFor('chauffeur');
  if (driverSvc) pushService(driverSvc, 'chauffeur', 'Transferts et déplacements sur place');
  else if (driverBook) addFromBooking(driverBook, 'chauffeur', 'Transferts et déplacements sur place');

  // Table locale ou activité
  const restaurantSvc = serviceBucket('restaurant')[0];
  const activiteSvc = serviceBucket('activite')[0];
  const restaurantBook = bookingFor('restaurant');
  const activiteBook = bookingFor('activite');
  if (restaurantSvc) pushService(restaurantSvc, 'restaurant', 'Repas / dégustation locale');
  else if (activiteSvc) pushService(activiteSvc, 'activite', "Activité sur place");
  else if (restaurantBook) addFromBooking(restaurantBook, 'restaurant', 'Repas / dégustation locale');
  else if (activiteBook) addFromBooking(activiteBook, 'activite', 'Activité sur place');

  // Un service générique complémentaire si le pack reste léger.
  if (items.length < 3) {
    const generic = services
      .filter((s) => !picked.includes(classifyService(`${s.titre || ''} ${s.typeservice || ''}`)))
      .sort((a, b) => (Number(a.prix) || Infinity) - (Number(b.prix) || Infinity))[0];
    if (generic) pushService(generic, classifyService(`${generic.titre || ''} ${generic.typeservice || ''}`), 'Service complémentaire');
  }

  if (items.length === 0) return null;

  // ── 3. Ajustement au budget ──
  let total = items.reduce((sum, i) => sum + i.price, 0);
  if (budget > 0 && total > budget) {
    // On retire les extras les plus chers (jamais l'hébergement) jusqu'à rentrer.
    while (total > budget && items.length > 1) {
      const removable = items
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => item.kind !== 'hebergement')
        .sort((a, b) => b.item.price - a.item.price);
      if (removable.length === 0) break;
      const { index, item } = removable[0];
      total -= item.price;
      items.splice(index, 1);
      notes.push(`« ${item.name} » retiré pour rester dans votre budget.`);
    }
  }

  const withinBudget = budget <= 0 || total <= budget;

  return {
    intent,
    pack: {
      destination: destination || 'votre destination',
      budget,
      currency,
      nights: intent.nights,
      days: intent.days,
      checkIn: intent.checkIn,
      checkOut: intent.checkOut,
      guests: intent.guests,
      items,
      total,
      withinBudget,
      notes,
      program: buildProgram({
        destination: destination || 'votre destination',
        checkIn: intent.checkIn,
        nights: intent.nights,
        days: intent.days,
        items,
      }),
    },
  };
}

// ── Réservation du pack complet ─────────────────────────────

export interface PackBuyer {
  id: number;
  name?: string | null;
  email?: string | null;
  telephone?: string | null;
}

export interface PackReservationResult {
  ok: boolean;
  orderIds: number[];
  hotels: number;
  appointments: number;
  merchants: number;
  error?: string;
}

function itemToCartItem(item: PackItem): CartItem {
  return {
    product: {
      id: item.cartId,
      name: item.name,
      price: item.price,
      original_price: item.price,
      media: item.image || '',
      stock: 0,
      product_type: 'service',
      owner_id: item.merchantId ?? 0,
      owner_name: item.merchantName || undefined,
      currency: item.currency,
    },
    quantity: 1,
  };
}

/**
 * Réserve l'intégralité du pack :
 * • une commande réelle PAR commerce (circuit Core, comme le panier) ;
 * • une réservation d'hébergement pour le logement ;
 * • un rendez-vous ZCalendar pour chaque service réservable ;
 * • une notification pour chaque commerce concerné ;
 * • le pack lui-même est archivé pour être retrouvé dans « Mes packs ».
 */
export async function reserveTravelPack(pack: TravelPack, buyer: PackBuyer): Promise<PackReservationResult> {
  const orderIds: number[] = [];
  let hotels = 0;
  let appointments = 0;
  const merchantIds = new Set<number>();
  const links = new Map<string, { orderId?: number; reservationId?: number; appointmentId?: number }>();
  const itemKey = (item: PackItem) => `${item.kind}-${item.entityId}`;

  pack.items.forEach((item) => { if (item.merchantId) merchantIds.add(item.merchantId); });

  // 1) Une commande par commerce : on sait ainsi à quelle commande
  //    appartient chaque prestation (indispensable pour le suivi).
  const byMerchant = new Map<number, PackItem[]>();
  pack.items.forEach((item) => {
    const owner = item.merchantId ?? 0;
    const bucket = byMerchant.get(owner);
    if (bucket) bucket.push(item);
    else byMerchant.set(owner, [item]);
  });

  let orderError: string | undefined;
  for (const [owner, items] of byMerchant.entries()) {
    const outcome = await placeOrderFromCart({
      items: items.map(itemToCartItem),
      currency: pack.currency,
      buyer: {
        id: buyer.id,
        name: buyer.name,
        email: buyer.email,
        telephone: buyer.telephone,
        address: `Pack ${pack.destination} — ${pack.checkIn} → ${pack.checkOut}`,
        city: pack.destination,
      },
    });
    orderIds.push(...outcome.orderIds);
    if (!outcome.ok && outcome.error) orderError = outcome.error;
    const firstOrderId = outcome.orderIds[0];
    items.forEach((item) => {
      links.set(itemKey(item), { ...(links.get(itemKey(item)) || {}), orderId: firstOrderId });
    });
    void owner;
  }

  // 2) Réservation d'hébergement (best-effort).
  for (const item of pack.items.filter((i) => i.kind === 'hebergement')) {
    try {
      const nights = item.nights || pack.nights;
      const perNight = nights > 0 ? item.price / nights : item.price;
      const { data } = await supabase
        .from('hebergement_reservations')
        .insert({
          merchant_id: item.merchantId,
          property_id: item.entityId,
          unit_id: null,
          customer_id: buyer.id,
          guest_name: buyer.name || 'Client',
          guest_email: buyer.email || null,
          guest_phone: buyer.telephone || null,
          check_in: item.checkIn || pack.checkIn,
          check_out: item.checkOut || pack.checkOut,
          guests: pack.guests,
          status: 'pending',
          price_per_night: Number(perNight.toFixed(2)),
          nights,
          subtotal: item.price,
          discount: 0,
          fees: 0,
          total: item.price,
          currency: item.currency,
          payment_status: 'unpaid',
          source: 'ai_chat',
          notes: `Pack ${pack.destination} composé par l'assistant IA`,
        })
        .select('id')
        .maybeSingle();
      if (data?.id) {
        hotels += 1;
        links.set(itemKey(item), { ...(links.get(itemKey(item)) || {}), reservationId: Number(data.id) });
      }
    } catch {
      /* best-effort : la commande reste valable */
    }
  }

  // 3) Rendez-vous pour les services réservables (guide, chauffeur…).
  for (const item of pack.items.filter((i) => i.bookingServiceId)) {
    try {
      const outcome = await attemptBookingAcross(
        [{
          id: Number(item.bookingServiceId),
          name: item.name,
          duration: 60,
          owner: item.merchantId,
          merchantName: item.merchantName,
        }],
        pack.checkIn,
        '10:00',
        {
          id: buyer.id,
          name: buyer.name || 'Client',
          email: buyer.email || '',
          telephone: buyer.telephone || '',
        },
      );
      if (outcome.ok) appointments += 1;
    } catch {
      /* best-effort */
    }
  }

  // 4) Notification dédiée « pack » pour chaque commerce.
  for (const item of pack.items) {
    if (!item.merchantId) continue;
    void pushNotification({
      userId: item.merchantId,
      senderId: buyer.id,
      type: item.kind === 'hebergement' ? 'booking' : 'order',
      publicationId: links.get(itemKey(item))?.orderId || orderIds[0] || null,
      message: `Pack ${pack.destination} (${pack.checkIn} → ${pack.checkOut}) : « ${item.name} » réservé par ${buyer.name || 'un client'}. À confirmer.`,
    });
  }

  // 5) Archivage du pack (retrouvé dans « Mes packs » + côté marchand).
  try {
    await supabase.from('order_headers').insert({
      customer_id: String(buyer.id),
      currency: pack.currency,
      payment_provider: 'travel_pack',
      status: 'pending',
      subtotal_items: pack.total,
      customer_notes: `Pack ${pack.destination} — ${pack.days} jour(s)`,
      recipient: {
        pack: {
          destination: pack.destination,
          budget: pack.budget,
          currency: pack.currency,
          nights: pack.nights,
          days: pack.days,
          checkIn: pack.checkIn,
          checkOut: pack.checkOut,
          guests: pack.guests,
          total: pack.total,
          notes: pack.notes,
          program: pack.program,
        },
        items: pack.items.map((item) => ({
          kind: item.kind,
          name: item.name,
          merchantId: item.merchantId,
          merchantName: item.merchantName,
          city: item.city,
          price: item.price,
          currency: item.currency,
          detail: item.detail,
          image: item.image,
          checkIn: item.checkIn || null,
          checkOut: item.checkOut || null,
          nights: item.nights || null,
          orderId: links.get(itemKey(item))?.orderId || null,
          reservationId: links.get(itemKey(item))?.reservationId || null,
          status: 'pending',
        })),
        customer: {
          name: buyer.name || null,
          email: buyer.email || null,
          telephone: buyer.telephone || null,
        },
      },
    });
  } catch {
    /* le pack reste réservé même si l'archivage échoue */
  }

  return {
    ok: orderIds.length > 0 || hotels > 0 || appointments > 0,
    orderIds,
    hotels,
    appointments,
    merchants: merchantIds.size,
    error: orderIds.length === 0 ? orderError : undefined,
  };
}

// ── Lecture des packs (espace client + commandes marchand) ──

export interface SavedPackItem {
  kind: PackItemKind;
  name: string;
  merchantId: number | null;
  merchantName: string | null;
  city: string | null;
  price: number;
  currency: string;
  detail: string | null;
  image: string | null;
  checkIn: string | null;
  checkOut: string | null;
  nights: number | null;
  orderId: number | null;
  reservationId: number | null;
  status: string;
}

export interface SavedPack {
  id: number;
  destination: string;
  budget: number;
  currency: string;
  nights: number;
  days: number;
  checkIn: string;
  checkOut: string;
  guests: number;
  total: number;
  status: string;
  notes: string[];
  program: PackProgramDay[];
  items: SavedPackItem[];
  createdAt: string;
}

interface PackHeaderRow {
  id: number;
  status: string | null;
  created_at: string | null;
  recipient: {
    pack?: Partial<SavedPack> & { notes?: string[]; program?: PackProgramDay[] };
    items?: SavedPackItem[];
  } | null;
}

function rowToPack(row: PackHeaderRow): SavedPack {
  const meta = row.recipient?.pack || {};
  return {
    id: Number(row.id),
    destination: String(meta.destination || 'Voyage'),
    budget: Number(meta.budget || 0),
    currency: String(meta.currency || 'MAD'),
    nights: Number(meta.nights || 1),
    days: Number(meta.days || (Number(meta.nights) || 1) + 1),
    checkIn: String(meta.checkIn || ''),
    checkOut: String(meta.checkOut || ''),
    guests: Number(meta.guests || 2),
    total: Number(meta.total || 0),
    status: String(row.status || 'pending'),
    notes: Array.isArray(meta.notes) ? meta.notes.map(String) : [],
    program: Array.isArray(meta.program) ? (meta.program as PackProgramDay[]) : [],
    items: Array.isArray(row.recipient?.items) ? (row.recipient.items as SavedPackItem[]) : [],
    createdAt: String(row.created_at || ''),
  };
}

/** Packs voyage du client connecté. */
export async function listMyTravelPacks(customerId: number): Promise<SavedPack[]> {
  const { data, error } = await supabase
    .from('order_headers')
    .select('id, status, created_at, recipient')
    .eq('customer_id', String(customerId))
    .eq('payment_provider', 'travel_pack')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return ((data || []) as PackHeaderRow[]).map(rowToPack);
}

/** Tous les packs archivés (indexés par numéro de commande). */
export async function listTravelPacksByOrder(): Promise<Record<number, SavedPack>> {
  const { data, error } = await supabase
    .from('order_headers')
    .select('id, status, created_at, recipient')
    .eq('payment_provider', 'travel_pack')
    .order('created_at', { ascending: false })
    .limit(200);
  if (error) throw error;
  const map: Record<number, SavedPack> = {};
  ((data || []) as PackHeaderRow[]).map(rowToPack).forEach((pack) => {
    pack.items.forEach((item) => {
      if (item.orderId) map[Number(item.orderId)] = pack;
    });
  });
  return map;
}